import { FunctionComponent } from 'react';
import { isTransportInformation, TransportInformationInputs } from '../sidebar/formInputs/TransportInformationInputs';
import { BulkStatusUpdateFormState, BulkStatusUpdateValidationFeedback } from './useBulkStatusUpdateForm';
import { CompletedStatusAtInput } from '../sidebar/CompletedStatusAtInput';
import { MeansOfTransportTypeInput } from '../sidebar/formInputs/MeansOfTransportTypeInput';
import { MeansOfTransportType } from '../../../sharedComponents/common.types';
import { LoadIdInput } from '../sidebar/formInputs/LoadIdInput';
import { CompoundManagementView, TransportInformation } from '../../compoundManagement.types';
import { FormattedMessage } from 'react-intl';
import { useAppSelector } from '../../../../../configuration/setup/hooks';
import { getLoadPreview } from '../../../reducers/compoundManagementSlice';

const DEFAULT_TIME_ZONE = 'etc/utc';

type BulkStatusUpdateFormProps = {
    disabled: boolean;
    formState: BulkStatusUpdateFormState;
    setDepartureCompletedAt: (departureCompletedAt: Date | string | undefined) => void;
    setLoadId: (loadId: string | undefined) => void;
    mergeChangedTransportInformation: (change: Partial<TransportInformation>) => void;
    validationErrorMessageIds: BulkStatusUpdateValidationFeedback;
};

export const BulkStatusUpdateForm: FunctionComponent<BulkStatusUpdateFormProps> = ({
    formState,
    setDepartureCompletedAt,
    setLoadId,
    mergeChangedTransportInformation,
    validationErrorMessageIds,
    disabled,
}) => {
    const loadPreview = useAppSelector(getLoadPreview);
    // for arriving and announced departing vehicles, a unique means of transport type should be present.
    // Therefore, the user is not allowed to select any other type.
    const motTypeIsAnnounced =
        loadPreview?.view === CompoundManagementView.INCOMING_VEHICLES ||
        loadPreview?.view === CompoundManagementView.OUTGOING_VEHICLES;
    const timeZone = loadPreview?.timeZone ?? DEFAULT_TIME_ZONE;

    const onMeansOfTransportTypeChanged = (meansOfTransportType: MeansOfTransportType | undefined) => {
        mergeChangedTransportInformation({ meansOfTransportType });
    };

    const statusAtLabelId =
        loadPreview?.view === CompoundManagementView.INCOMING_VEHICLES
            ? 'outboundOrderBook.compoundManagement.table.heading.arrivalDate'
            : 'outboundOrderBook.compoundManagement.table.heading.departureDate';
    return (
        <>
            <InfoBox />
            <div className="display-grid grid-cols-2 padding-15 column-gap-15 row-gap-0 rounded bg-lightest">
                <CompletedStatusAtInput
                    labelId={statusAtLabelId}
                    timeZone={timeZone}
                    disabled={disabled}
                    statusDate={formState.statusAt}
                    onChangeStatusDate={setDepartureCompletedAt}
                    validationErrorId={validationErrorMessageIds.statusAt}
                />
                <LoadIdInput
                    disabled={disabled}
                    loadId={formState.transportInformation.loadId}
                    setLoadId={setLoadId}
                    validationErrorMessageId={validationErrorMessageIds.loadId}
                />
                <hr className="width-100pct grid-column-span-2" />
                <MeansOfTransportTypeInput
                    disabled={disabled || motTypeIsAnnounced}
                    value={formState.transportInformation.meansOfTransportType}
                    onMeansOfTransportTypeChanged={onMeansOfTransportTypeChanged}
                    validationErrorId={validationErrorMessageIds.meansOfTransportType}
                />
                <div />
                {isTransportInformation(formState.transportInformation) && (
                    <TransportInformationInputs
                        transportInformation={formState.transportInformation}
                        setTransportInformation={mergeChangedTransportInformation}
                        validationErrorMessageIds={validationErrorMessageIds}
                        disabled={disabled}
                    />
                )}
            </div>
        </>
    );
};

const InfoBox: FunctionComponent = () => (
    <div className="alert alert-info display-flex gap-10 margin-bottom-25">
        <span className="text-color-info text-size-h4 rioglyph rioglyph-info-sign" />
        <div>
            <strong className="text-size-16">
                <FormattedMessage id="outboundOrderBook.compoundManagement.bulkStatusUpdate.noUpdateInfo1" />
            </strong>
            <span className="margin-left-4">
                <FormattedMessage id="outboundOrderBook.compoundManagement.bulkStatusUpdate.noUpdateInfo2" />
            </span>
        </div>
    </div>
);
